import {
  type ConditionModel,
  type ConditionStatuses,
  conditionStatuses,
  outsideConditionStatuses,
} from '@ctw/shared/api/fhir/models/condition';
import type {
  FilterItem,
  FilterOptionTag,
} from '@ctw/shared/components/filter-bar/filter-bar-types';
import type { PredicateMap } from '@ctw/shared/hooks/use-filtered-sorted-data';
import { uniqueValues } from '@ctw/shared/utils/filters';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import {
  faClipboardCheck,
  faClipboardList,
  faEye,
  faEyeSlash,
} from '@fortawesome/pro-solid-svg-icons';
import { intersectionWith } from 'lodash-es';
import type { ConditionViewOptions } from './views';

const conditionHccPredicate = (selected: boolean, item: ConditionModel): boolean =>
  !selected || item.hasHCCv28Codes;

export const conditionFilterPredicates: PredicateMap<FilterOptionTag['predicate']> = {
  showOnlyHcc: (selected: boolean, item: object): boolean =>
    conditionHccPredicate(selected, item as ConditionModel),
};

export function conditionFilters(
  conditions: Array<ConditionModel>,
  showAllStatuses: boolean,
  onlyShowOptionsForStatus?: ConditionViewOptions,
): Array<FilterItem> {
  const filters: Array<FilterItem> = [
    {
      key: 'showOnlyHcc',
      type: 'tag',
      belowDivider: true,
      display: ({ listView }) => (listView ? 'Show HCCs Only' : 'HCCs Only'),
      predicate: conditionFilterPredicates.showOnlyHcc,
      icon: faEye,
      toggleDisplay: 'show all conditions',
      toggleIcon: faEyeSlash,
    },
  ];

  let statusesToShow: Array<ConditionStatuses> = showAllStatuses
    ? [...conditionStatuses.filter((status) => status !== 'Entered in Error')]
    : [...outsideConditionStatuses];

  if (onlyShowOptionsForStatus) {
    switch (onlyShowOptionsForStatus) {
      case 'Current':
        statusesToShow = intersectionWith(
          statusesToShow,
          ['Active', 'Pending', 'Unknown'],
          (a, b) => a === b,
        );
        break;
      case 'Past':
        statusesToShow = intersectionWith(
          statusesToShow,
          ['Inactive', 'Refuted'],
          (a, b) => a === b,
        );
        break;
      default:
        break;
    }
  }

  filters.push(
    {
      key: 'displayStatus',
      type: 'checkbox',
      icon: faClipboardCheck,
      display: 'Status',
      // Create new array as these other ones are readonly.
      values: statusesToShow,
    },
    {
      key: 'ccsChapter',
      type: 'checkbox',
      icon: faClipboardList,
      display: 'Category',
      values: uniqueValues(conditions, 'ccsChapter'),
    },
    {
      key: 'typeDisplay',
      type: 'checkbox',
      icon: faClock,
      display: 'Type',
      values: uniqueValues(conditions, 'typeDisplay').sort((a, b) => {
        // We have to sort these filters in a specific order. Acute, Chronic, Chronic and Acute...
        const scores: Record<string, number | undefined> = {
          acute: 4,
          chronic: 3,
          'chronic and acute': 2,
        };
        const aScore = scores[a.toLowerCase()] ?? 0;
        const bScore = scores[b.toLowerCase()] ?? 0;
        if (aScore > bScore) {
          return -1;
        }
        if (bScore > aScore) {
          return 1;
        }
        return 0;
      }),
    },
  );

  return filters;
}
